import { ChangeDetectionStrategy, Component, Input, OnDestroy, OnInit, ViewChild, signal, computed } from '@angular/core';
import { toObservable, toSignal } from '@angular/core/rxjs-interop';
import { UntypedFormBuilder } from '@angular/forms';
import { DomSanitizer, SafeUrl } from '@angular/platform-browser';
import { DialogAction, DialogCloseResult, DialogRef, DialogResult, DialogService } from '@progress/kendo-angular-dialog';
import { DataStateChangeEvent, GridDataResult } from '@progress/kendo-angular-grid';
import { DataResult, State, process } from '@progress/kendo-data-query';
import { BehaviorSubject, combineLatest, map, Observable, Subject, switchMap, takeUntil } from 'rxjs';
import { DetailsComponent } from './details.component';
import { Flow } from '../obj/Flow';
import { ApiService } from '../services/api.service';
import { MsalHelperService } from '../services/msal-helper.service';
import { getTriggerIconUrl, getTriggerTypeLabel } from '../flows/trigger-icon.util';

@Component({
    selector: 'app-teams-flows',
    templateUrl: './teams.flows.component.html',
    styleUrl: './teams.flows.component.scss',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: false
})
export class TeamsFlowsComponent implements OnInit, OnDestroy {

  private destroy$ = new Subject<void>(); // Destroy notifier
  
  // @Input() monitored$: Observable<any[]>;
  @ViewChild('grid', {static: true}) public grid;

  state = signal<State>({
      skip: 0,
      take: 100, //this.flows.length,

      // Initial filter descriptor
      group: [],
      filter: {
          logic: 'and',
          filters: [] //[{ field: 'ProductName', operator: 'contains', value: 'Chef' }]
      },
      sort: [
          {
              'field': 'displayName',
              'dir': 'asc'
          }
      ]
  });
  //state$: BehaviorSubject<State> = new BehaviorSubject(this.state);
  private _data = signal<any[]>([]);
  /** Becomes true once apiService.flow$ emits its first payload — drives the grid's loading-vs-empty distinction. */
  protected loaded = signal(false);
  protected type = signal<'monitor'|'request'|'all'>("monitor"); // monitor or all

  @Input() set Type(newType: 'monitor' | 'request' | 'all') {
    this.type.set(newType);
  }

  @Input() hideToolbar = false;

  private _searchSource$ = new BehaviorSubject<BehaviorSubject<string>>(null);

  @Input() set externalSearch$(subject: BehaviorSubject<string>) {
    if (subject) {
      this._searchSource$.next(subject);
    }
  }

  //public view$: Observable<DataResult>;
  public view = computed<GridDataResult>(()=>{

    const search = this.search();
    const type = this.type();
    const data = (this._data() || [])
      .filter( (flow)=> {
        return !!flow.name;
      })
      .filter( (flow)=> {
        return (
            !search ||
            includes((flow.displayName), search) ||
            includes((flow.ref_environment?.displayName), search) 
            // ||
            // includes((flow.lastModifiedTime), search) ||
            // includes((flow.runLast), search)
        );
      })
      .filter( (flow)=> {
        return (
            (type == 'monitor' && flow.monitor) ||
            (type == 'request' && flow.triggerType == 'Request' && flow.triggerKind == 'Http') ||
            type == 'all'
        );
      });

    return process(data, this.state())
  })
  public buttonCount = 0;
  public search$: BehaviorSubject<string>;
  public search: any;

  protected typeLabels: Record<string, string> = {
    monitor: 'Flows (monitored)',
    request: 'Flows (requests)',
    all: 'Flows (all)'
  };


  protected types = [
    {
      text: this.typeLabels['monitor'],
      click: (): void => {
        this.type.set('monitor');
      },
    },
    {
      text: this.typeLabels['all'],
      click: (): void => {
        this.type.set('all');
      },
    },
    {
      text: this.typeLabels['request'],
      click: (): void => {
        this.type.set('request');
      },
    },
  ];

  constructor(
    private formBuilder: UntypedFormBuilder,
    private dialogService: DialogService,
    private apiService: ApiService,
    private msalHelperService: MsalHelperService,
    private sanitizer: DomSanitizer
  ) {
    this.search$ = new BehaviorSubject('');
    this._searchSource$.next(this.search$);
    this.search = toSignal(
      this._searchSource$.pipe(switchMap(s => s ?? this.search$)),
      { initialValue: '' }
    );
  }

  ngOnInit(): void {

    this.apiService.flow$.pipe(
      map((data)=>{
        this._data.set(data);
        this.loaded.set(true);
      }),
      takeUntil(this.destroy$)
    ).subscribe();
    // this.view$ = combineLatest([this.monitored$, this.state$]).pipe(
    //   map(([data, state])=>{

    //     return process(data, state);
        
    //   }),
    //   takeUntil(this.destroy$)
    // )    
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  private triggerIconSafeCache = new Map<string, { rawUrl: string; safeUrl: SafeUrl }>();
  public getTriggerIcon(flow: any): SafeUrl {
    const url = getTriggerIconUrl(flow);
    const key = flow?.flowName || flow?.name || flow?.RowKey || JSON.stringify(flow?.triggers || []);
    const cached = this.triggerIconSafeCache.get(key);
    if (cached && cached.rawUrl === url) return cached.safeUrl;
    const safeUrl = this.sanitizer.bypassSecurityTrustUrl(url);
    this.triggerIconSafeCache.set(key, { rawUrl: url, safeUrl });
    return safeUrl;
  }
  public getTriggerLabel(flow: any): string {
    return getTriggerTypeLabel(flow);
  }

  public getSuccessRateLabel(flow: any): string {
    const runs = flow?.periodRuns ?? 0;
    const succeeded = flow?.periodSuccessed ?? (runs - (flow?.periodFailed ?? 0));
    if (runs > 0) return `${((succeeded / runs) * 100).toFixed(1)}%`;
    return 'N/A';
  }

  public getSuccessRateTone(flow: any): 'healthy' | 'warning' | 'critical' {
    const runs = flow?.periodRuns ?? 0;
    const succeeded = flow?.periodSuccessed ?? (runs - (flow?.periodFailed ?? 0));
    if (runs === 0) return 'warning';
    const rate = succeeded / runs;
    if (rate >= 0.95) return 'healthy';
    if (rate >= 0.75) return 'warning';
    return 'critical';
  }

  public dataStateChange(state: DataStateChangeEvent): void {

    const oldState = this.state();

    if (state.sort && state.sort.length == 1) {
      // only on 1

      var field = state.sort[0].field;

      if (!oldState.sort || field != oldState.sort[0].field) {

        var desc = this.grid.columns.filter((c: any) => { return c.field == field && (c.filter == "date" || c.filter == "number"); }).length > 0;

        if (desc) {
          state.sort[0].dir = "desc";
        }
      }
    }

    const newState = {
      filter: (typeof (state.filter) == "function" ? oldState.filter : state.filter),
      group: state.group,
      sort: state.sort,
      skip: state.skip,
      take: state.take
    }

    this.state.set(newState);

    // setTimeout(() => {
    //   this.storageDexieService.putConfig(
    //     Settings.SETTING_GRIDSTATE,
    //     {
    //       'key': Settings.SETTING_GRIDSTATE,
    //       value: newState
    //     });
    // }, 1);
    //this.view = process(this.flows, this.state);
  }

  filterBy(field: string, value: string, toggle: boolean = true) {
    var state: any = this.state();
    if (value) {
      if (state.filter.filters && state.filter.filters.length && state.filter.filters[0].value == value && toggle) {
        state.filter.filters = [];
      }
      else {
        state.filter.filters = [{
          "field": field,
          "operator": "contains",
          "value": value
        }];
        state.skip = 0;
      }
    }
    else {
      state.filter.filters = [];
    }
    this.dataStateChange(state);
  }

  // filterByTriggerKind(triggerKind: string) {
  //     this.filterBy("triggerKind", triggerKind);
  // }

  filterByFlowState(flowState: string) {
    this.filterBy("state", flowState);
  }

  private getGraphqlFlowKey(flow: any): string {
    const environmentName = flow?.environmentName;
    const flowName = flow?.flowName;

    if (environmentName && flowName) {
      return `${environmentName}.${flowName}`;
    }

    return flow?.RowKey || '';
  }

  async editFlow(flow:any) {

    const rowKey = this.getGraphqlFlowKey(flow);

    try {

      const dialog: DialogRef = this.dialogService.open({
        title: 'Flow Rules',      
        content: DetailsComponent,
        // actions: [
        //     { text: 'Save', primary: true, disabled: true },
        //     { text: 'Cancel' }
        // ],
        width: 508,
        height: 470,
        minWidth: 400,
      });

      const details = (dialog.content.instance as DetailsComponent);

      const user = await this.msalHelperService.getUser();
      const queryResult = await this.apiService.queryFlow(rowKey);
      const flow1 = queryResult.data?.flow;

      if (!flow1) {
        return;
      }

      details.flow = flow1;
      details.form.setValue({
        monitor: flow1.monitor,      

        notifyOnFail: !!flow1.rule_notify_onfail,
        notifyOnMissed: (flow1.rule_notify_onmissingdays || 0) > 0,
        missingDays: flow1.rule_notify_onmissingdays || 0,

        email: flow1.rule_notify_email || user?.username
      });    

      const result: any = await dialog.result.toPromise();


      if (result instanceof DialogCloseResult) {
        return;
      }

      const v = result;

      await this.apiService.mutateFlow(`${flow1.RowKey}`, {
          monitor: v.monitor,
          rule_notify_onfail: !!v.notifyOnFail,
          rule_notify_onmissingdays: v.notifyOnMissed ? v.missingDays : 0,
          rule_notify_email: v.email
      }, (update)=>{
      });

      // Patch the in-memory flow$ so the grid reflects changes without a full reload.
      const current = this.apiService.flow$.value || [];
      const idx = current.findIndex((f: any) => (f.name || f.RowKey) === flow1.RowKey);
      if (idx !== -1) {
        const updated = [...current];
        updated[idx] = {
          ...updated[idx],
          monitor: v.monitor,
          rule_notify_onfail: !!v.notifyOnFail,
          rule_notify_onmissingdays: v.notifyOnMissed ? v.missingDays : 0,
          rule_notify_email: v.email
        };
        this.apiService.flow$.next(updated);
      }

    }
    catch {

    }


  }
}

export function includes(source: string = '', search: string = ''): boolean {
  if (!source || !search) {
    return false;
  }
  return source.toLocaleLowerCase().indexOf(search.toLocaleLowerCase()) !== -1;
}
